import { useEffect, useRef, useState } from 'react';
import {
  Flex,
  Icon,
  InlineStack,
  Link,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
} from '@customerio/pluma-components/react';
import { CredentialKeyPair, CredentialsSelect } from '@/features/integrations';
import { useAnalytics } from '@/services/analytics';
import type { ApiCredential } from '@/services/db/api-credential/entity';
import { COPY_SOURCE, CREDENTIAL_TYPE, SNIPPET_CREDENTIAL_TYPE } from '../analytics';
import type { identifySnippet, SnippetKind } from '../snippets';
import { SnippetSteps } from './snippet-steps';

type JavascriptSnippetPanelProps = {
  credentials: readonly ApiCredential[];
  assetHost: string;
  mergeOptionsHref: string;
  isAnonymousActivityMerged: boolean;
  /**
   * The merge notice states the setting in prose, so it waits for the read to
   * succeed. `isAnonymousActivityMerged` is false both while the read is in
   * flight and after it fails, and telling someone their anonymous activity is
   * not merged when it is would be worse than saying nothing.
   */
  hasMergeSettingLoaded: boolean;
  /**
   * Scroll this panel into view on mount. The activity-log empty state links
   * here to show someone their tracking snippet, and the panel sits below the
   * credentials panel.
   */
  shouldScrollIntoView: boolean;
  identify: Parameters<typeof identifySnippet>[0];
};

/**
 * The JavaScript install guide.
 *
 * The snippet embeds one credential's site ID, so the three steps only appear
 * once a credential is picked. A workspace with a single credential has it
 * picked already and never sees the picker.
 */
export function JavascriptSnippetPanel({
  credentials,
  assetHost,
  mergeOptionsHref,
  isAnonymousActivityMerged,
  hasMergeSettingLoaded,
  shouldScrollIntoView,
  identify,
}: JavascriptSnippetPanelProps) {
  const { track } = useAnalytics();
  const panelRef = useRef<HTMLDivElement>(null);
  const [pickedCredentialId, setPickedCredentialId] = useState<string | null>(null);
  const [onlyCredential] = credentials;
  const hasSingleCredential = credentials.length === 1;

  const selectedCredential = hasSingleCredential
    ? (onlyCredential ?? null)
    : (credentials.find((credential) => credential.id === pickedCredentialId) ?? null);

  useEffect(() => {
    if (shouldScrollIntoView) {
      panelRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [shouldScrollIntoView]);

  const trackSnippetCopy = (kind: SnippetKind) => {
    void track('integration', 'apiCredentialsCopied', {
      'API Credential Type': SNIPPET_CREDENTIAL_TYPE,
      'API Credential Copied': kind,
      Source: COPY_SOURCE,
    });
  };

  return (
    <Panel ref={panelRef}>
      <PanelHeader id="snippet">
        <PanelTitle>JavaScript Snippet</PanelTitle>
      </PanelHeader>

      <PanelSection>
        <Paragraph mb="200">
          Alternatively, you can send data through your users&apos; web browsers with our JavaScript snippet.
        </Paragraph>

        {hasMergeSettingLoaded ? (
          <MergeNotice isMerged={isAnonymousActivityMerged} mergeOptionsHref={mergeOptionsHref} />
        ) : null}

        <InlineStack alignBlock="stretch" mt="200" gap="200">
          {hasSingleCredential ? null : (
            <CredentialsSelect
              credentials={credentials}
              selectedCredentialId={pickedCredentialId}
              onSelect={(credential) => setPickedCredentialId(credential.id)}
              label="Credentials for"
            />
          )}

          {selectedCredential == null ? null : (
            <CredentialKeyPair credential={selectedCredential} credentialType={CREDENTIAL_TYPE} source={COPY_SOURCE} />
          )}
        </InlineStack>
      </PanelSection>

      {selectedCredential == null ? null : (
        <SnippetSteps
          siteId={selectedCredential.site_id}
          assetHost={assetHost}
          identify={identify}
          onCopy={trackSnippetCopy}
        />
      )}
    </Panel>
  );
}

type MergeNoticeProps = {
  isMerged: boolean;
  mergeOptionsHref: string;
};

/** Whether anonymous activity merges into profiles, and where to change that. */
function MergeNotice({ isMerged, mergeOptionsHref }: MergeNoticeProps) {
  return (
    <Flex alignItems="center" gap="050">
      <Icon name={isMerged ? 'check-circle' : 'close'} color={isMerged ? 'success' : 'critical'} />
      <Paragraph variant="secondary" size="xxs">
        Anonymous activity will {isMerged ? '' : 'not '}be merged with profiles, because{' '}
        <Link variant="secondary" href={mergeOptionsHref}>
          this setting is {isMerged ? 'enabled' : 'disabled'}
        </Link>
        .
      </Paragraph>
    </Flex>
  );
}
